import { Box, Stack, styled } from '@mui/material'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { BottomDrawer } from '$common/bottom-drawer'
import { ContextMenuItem } from '$comp/_app/components/chat/components/body/components/context-menu/components'
import { EditOutlinedIcon, BookmarkIcon } from '$common/icons'
import { TrashIcon } from '$common/icons/trash'
import { ShareIcon } from '$common/icons/share'
import { ReportIcon } from '$common/icons/report'
import { showBookmarkSelectionListModal } from '$comp/modals/bookmark-selection-list-modal'
import { showReportSendSnackbar } from '$comp/snackbars'
import { t } from '$i18n'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { getContentCollection } from '$logic/bookmark'
import { BookmarkFillIcon } from '$common/icons/bookmark-fill'
import { showDeleteContentConfirmModal } from '$comp/modals/confirmation-dialog'
import { ActionButton } from './action-button'
import { Separator } from './separator'

const Root = styled(Stack)`
  padding: 20px 5px 5px;
`

const StyledEditIcon = styled(EditOutlinedIcon)`
  margin-left: 12px;
`
const StyledTrashIcon = styled(TrashIcon)`
  margin-left: 12px;
`

export interface Props {
  isOwner: boolean
  hasEdit: boolean
  hasDelete: boolean
  hasShare: boolean
  sendEditEvent: () => unknown
  sendShareEvent: () => unknown
  sendDeleteEvent: () => unknown
  bookmarkData: { contentUri: string; cover: string } | null
  isOpen?: boolean
}
export function WappBarMenu({
  isOwner,
  hasEdit,
  hasDelete,
  hasShare,
  sendEditEvent,
  sendShareEvent,
  sendDeleteEvent,
  bookmarkData,
  isOpen = false,
}: Props) {
  const { value: oldCollections } = useAsyncFunction(
    () => getContentCollection(bookmarkData?.contentUri || ''),
    [bookmarkData?.contentUri],
  )

  return (
    <BottomDrawer isOpen={isOpen} onClose={closeCurrentModal}>
      <Root>
        <Box
          sx={{
            height: '82px',
            display: 'flex',
            width: '100%',
            justifyContent: 'center',
            marginBottom: '20px',
          }}
          gap="34px"
        >
          {bookmarkData && (
            <ActionButton
              text={
                oldCollections?.length
                  ? t('bookmarkModal.bookmarked')
                  : t('bookmarkModal.bookmark')
              }
              onClick={() => {
                closeCurrentModal()
                showBookmarkSelectionListModal({
                  oldCollections: oldCollections ?? [],
                  bookmarkData,
                })
              }}
            >
              {oldCollections?.length ? (
                <BookmarkFillIcon />
              ) : (
                <BookmarkIcon sx={{ color: 'secondary.main' }} />
              )}
            </ActionButton>
          )}
          {hasShare && (
            <ActionButton
              text={t('menu.common.share')}
              onClick={async () => {
                await closeCurrentModal()
                sendShareEvent()
              }}
            >
              <ShareIcon sx={{ color: 'secondary.main' }} />
            </ActionButton>
          )}
          {!isOwner && (
            <ActionButton
              text={t('bookmarkModal.report')}
              onClick={showReportSendSnackbar}
            >
              <ReportIcon sx={{ color: 'secondary.main' }} />
            </ActionButton>
          )}
        </Box>
        {(hasDelete || hasEdit) && (
          <Stack gap="8px">
            <Separator />
            {hasEdit && (
              <ContextMenuItem
                onClick={async () => {
                  await closeCurrentModal()
                  sendEditEvent()
                }}
                text={t('bookmarkModal.edit')}
              >
                <StyledEditIcon sx={{ color: 'secondary.main' }} />
              </ContextMenuItem>
            )}
            {hasDelete && (
              <ContextMenuItem
                onClick={async () => {
                  await closeCurrentModal()
                  await showDeleteContentConfirmModal(sendDeleteEvent)
                }}
                text={t('bookmarkModal.remove')}
              >
                <StyledTrashIcon sx={{ color: 'secondary.main' }} />
              </ContextMenuItem>
            )}
          </Stack>
        )}
      </Root>
    </BottomDrawer>
  )
}
